import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { 
  Palette, X, Check, Sparkles, Sliders, Moon, Sun, 
  Monitor, Flame, Zap, Eye, RotateCcw, Shield
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const THEME_PALETTES = [
  { id: "orange", name: "Tangerine Orange", primary: "#FF4F00", primaryHover: "#FF6B2B", glow: "rgba(255,79,0,0.6)", type: "cs2" },
  { id: "green", name: "Emerald Green", primary: "#10B981", primaryHover: "#34D399", glow: "rgba(16,185,129,0.6)", type: "tactical" },
  { id: "red", name: "Crimson Red", primary: "#EF4444", primaryHover: "#F87171", glow: "rgba(239,68,68,0.6)", type: "aggressive" },
  { id: "blue", name: "Cyber Blue", primary: "#00A6FF", primaryHover: "#38BDF8", glow: "rgba(0,166,255,0.6)", type: "cyber" },
  { id: "purple", name: "Royal Purple", primary: "#A855F7", primaryHover: "#C084FC", glow: "rgba(168,85,247,0.6)", type: "exclusive" },
  { id: "cyan", name: "Neon Cyan", primary: "#00F0FF", primaryHover: "#38F8FF", glow: "rgba(0,240,255,0.6)", type: "neon" },
  { id: "amber", name: "Sunset Gold", primary: "#F59E0B", primaryHover: "#FBBF24", glow: "rgba(245,158,11,0.6)", type: "gold" },
  { id: "pink", name: "Cyberpunk Pink", primary: "#EC4899", primaryHover: "#F472B6", glow: "rgba(236,72,153,0.6)", type: "cyber" },
];

const BACKGROUND_PRESETS = [
  { id: "cosmic_nebula", label: "Cosmic Nebula & Stars", icon: "🌌", desc: "Particule stelare și nebuloasă animată" },
  { id: "matrix_rain", label: "Matrix Digital Rain", icon: "🟢", desc: "Cod binar și caractere verzi căzătoare" },
  { id: "cs_smoke_fire", label: "CS2 Inferno & Smoke", icon: "🔥", desc: "Scântei și jerbe de foc în mișcare" },
  { id: "molten_gold", label: "Molten Gold Particles", icon: "✨", desc: "Praf auriu plutitor cu străluciri" },
  { id: "none", label: "Dark Minimalist (Solid)", icon: "🌑", desc: "Fundal mat întunecat fără animații" },
];

export default function ThemeCustomizerDrawer({ isOpen, onClose }) {
  const { theme, setTheme } = useTheme();
  const [activeBg, setActiveBg] = useState(() => localStorage.getItem("idg_bg_type") || "cosmic_nebula");
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && isOpen) {
        if (confirmDialog) setConfirmDialog(null);
        else if (onClose) onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, confirmDialog, onClose]);

  // Handle Palette Switch with Confirmation Dialog
  const handleSelectTheme = (palette) => {
    setConfirmDialog({
      title: "Aplică Schema de Culori",
      message: `Ești sigur că vrei să schimbi tema vizuală principală în "${palette.name}"?`,
      palette: palette
    });
  };

  const handleConfirmTheme = () => {
    if (confirmDialog?.palette) {
      setTheme(confirmDialog.palette.id);
      localStorage.setItem("tf_theme", confirmDialog.palette.id);
      
      const root = document.documentElement;
      root.style.setProperty("--tf-primary", confirmDialog.palette.primary);
      root.style.setProperty("--tf-primary-hover", confirmDialog.palette.primaryHover);
      root.style.setProperty("--tf-glow", confirmDialog.palette.glow);
      
      window.dispatchEvent(new CustomEvent("idg:theme-changed", { detail: confirmDialog.palette }));
    }
    setConfirmDialog(null);
  };

  // Handle Background Switch
  const handleSelectBg = (bgId) => {
    setActiveBg(bgId);
    localStorage.setItem("idg_bg_type", bgId);
    window.dispatchEvent(new CustomEvent("idg:bg-changed", { detail: bgId }));
  };

  const handleResetDefaults = () => {
    setTheme("orange");
    setActiveBg("cosmic_nebula");
    localStorage.setItem("tf_theme", "orange");
    localStorage.setItem("idg_bg_type", "cosmic_nebula");
    
    const root = document.documentElement;
    root.style.setProperty("--tf-primary", "#FF4F00");
    root.style.setProperty("--tf-primary-hover", "#FF6B2B");
    root.style.setProperty("--tf-glow", "rgba(255,79,0,0.6)");
    
    window.dispatchEvent(new CustomEvent("idg:bg-changed", { detail: "cosmic_nebula" }));
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  const drawerContent = (
    <div className="fixed inset-0 z-[999999] flex justify-end select-none animate-fadeIn">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={() => { if (!confirmDialog && onClose) onClose(); }}
      />

      {/* Slide-in Drawer Container */}
      <div 
        id="themeSide"
        className="relative w-full max-w-md h-full bg-[#090b12] border-l border-white/10 shadow-[-20px_0_80px_rgba(0,0,0,0.9)] flex flex-col z-10 animate-slideLeft overflow-hidden"
      >
        {/* Top Header Laser Bar */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[var(--tf-primary,#ff4f00)] to-transparent" />

        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between bg-black/40">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--tf-primary,#ff4f00)]/15 border border-[var(--tf-primary,#ff4f00)]/40 flex items-center justify-center text-[var(--tf-primary,#ff4f00)] shadow-[0_0_15px_rgba(255,79,0,0.3)]">
              <Palette className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-chakra font-black text-sm uppercase tracking-wider text-white">
                PERSONALIZARE TEMĂ
              </h2>
              <p className="text-[10px] font-mono text-zinc-400">
                // Culori, fundaluri canvas & efecte vizuale
              </p>
            </div>
          </div>

          <button 
            id="closeThemeEditor"
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition-all cursor-pointer"
            title="Închide (ESC)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-5 overflow-y-auto flex-1 space-y-6">
          
          {/* 1. Accent Color Palette Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-chakra font-black uppercase tracking-wider text-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--tf-primary,#ff4f00)]" />
                CULOARE PRINCIPALĂ ACCENT
              </label>
              <span className="text-[10px] font-mono text-zinc-500 uppercase">
                {THEME_PALETTES.find(p => p.id === theme)?.name || "Personalizat"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {THEME_PALETTES.map((p) => {
                const isActive = theme === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleSelectTheme(p)}
                    className={`relative p-3 rounded-lg border text-left flex items-center gap-3 transition-all cursor-pointer ${
                      isActive 
                        ? "bg-white/[0.06] border-white text-white shadow-lg ring-1 ring-white/30"
                        : "bg-black/40 border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
                    }`}
                  >
                    <span 
                      className="w-7 h-7 rounded-md shrink-0 flex items-center justify-center shadow-md"
                      style={{ 
                        backgroundColor: p.primary, 
                        boxShadow: `0 0 14px ${p.glow}` 
                      }}
                    >
                      {isActive && <Check className="w-4 h-4 text-black stroke-[3]" />}
                    </span>
                    <div className="overflow-hidden">
                      <div className="font-chakra font-black text-xs uppercase truncate leading-tight">
                        {p.name}
                      </div>
                      <div className="text-[9px] font-mono text-zinc-500 uppercase mt-0.5">
                        {p.type}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. Interactive Canvas Backgrounds */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-chakra font-black uppercase tracking-wider text-white flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                EFECT CANVAS & FUNDAL ANIMAT
              </label>
            </div>

            <div className="space-y-2">
              {BACKGROUND_PRESETS.map((bg) => {
                const isSelected = activeBg === bg.id;
                return (
                  <button
                    key={bg.id}
                    type="button"
                    onClick={() => handleSelectBg(bg.id)}
                    className={`w-full p-3 rounded-lg border text-left flex items-center justify-between transition-all cursor-pointer ${
                      isSelected
                        ? "bg-[var(--tf-primary,#ff4f00)]/15 border-[var(--tf-primary,#ff4f00)] text-white shadow-[0_0_20px_rgba(255,79,0,0.15)]"
                        : "bg-black/40 border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{bg.icon}</span>
                      <div>
                        <div className="font-chakra font-black text-xs uppercase">
                          {bg.label}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500">
                          {bg.desc}
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-[var(--tf-primary,#ff4f00)] text-black flex items-center justify-center">
                        <Check className="w-3 h-3 stroke-[3]" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3. Quick System Info */}
          <div className="p-3.5 rounded-lg bg-black/50 border border-white/10 text-xs font-mono text-zinc-400 space-y-1">
            <div className="flex items-center justify-between text-zinc-500 text-[10px] uppercase">
              <span>Sincronizare Setări</span>
              <span className="text-emerald-400">ACTIVĂ (LOCAL & CLOUD)</span>
            </div>
            <p className="text-[11px] text-zinc-400">
              Preferințele de temă și fundal sunt salvate automat în profilul tău pentru toate sesiunile viitoare.
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-5 border-t border-white/10 bg-black/40 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleResetDefaults}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-400 hover:text-white font-chakra font-bold text-xs uppercase transition-all cursor-pointer"
            title="Resetează la tema implicită"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>RESET</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-4 rounded-lg font-chakra font-black text-xs uppercase tracking-wider text-black bg-[var(--tf-primary,#ff4f00)] hover:brightness-110 shadow-lg transition-all text-center cursor-pointer"
          >
            SALVEAZĂ & ÎNCHIDE
          </button>
        </div>

        {/* Confirmation Modal */}
        {confirmDialog && (
          <div className="absolute inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-6 text-center animate-fadeIn">
            <div className="max-w-xs w-full bg-[#0c0f17] border border-white/20 rounded-xl p-5 shadow-2xl space-y-4">
              <div 
                className="w-12 h-12 rounded-xl mx-auto flex items-center justify-center shadow-lg"
                style={{ 
                  backgroundColor: confirmDialog.palette.primary, 
                  boxShadow: `0 0 20px ${confirmDialog.palette.glow}` 
                }}
              >
                <Palette className="w-6 h-6 text-black" />
              </div>

              <div>
                <h3 className="font-chakra font-black text-base uppercase text-white">
                  {confirmDialog.title}
                </h3>
                <p className="text-xs font-mono text-zinc-400 mt-1">
                  {confirmDialog.message}
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setConfirmDialog(null)}
                  className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-chakra font-bold uppercase text-zinc-300 transition-colors"
                >
                  Anulează
                </button>
                <button
                  type="button"
                  onClick={handleConfirmTheme}
                  className="flex-1 py-2 rounded-lg font-chakra font-black text-xs uppercase text-black bg-[var(--tf-primary,#ff4f00)] hover:brightness-110 transition-all shadow-md"
                >
                  Aplică
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(drawerContent, document.body);
}
